import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Crown, Trash2, UserPlus, Users, Receipt, IndianRupee, Wallet, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { memberBalance, listMembershipReceipts } from "@/lib/membership";
import { printInvoiceById, downloadInvoiceById } from "@/lib/receiptActions";
import MembershipCollectDialog from "@/components/MembershipCollectDialog";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const PAYMENT_BADGE = {
    cash: { bg: "#FDF3DC", text: "#9A7B10", label: "Cash" },
    upi: { bg: "#E0EDFA", text: "#3B82C4", label: "UPI" },
    card: { bg: "#FBE4EF", text: "#D9468F", label: "Card" },
    razorpay: { bg: "#FDE8EC", text: MAROON, label: "Razorpay" },
    wallet: { bg: "#EDE8F5", text: "#7C3AED", label: "Wallet" },
};

function PaymentBadge({ mode, status }) {
    const m = PAYMENT_BADGE[mode] || { bg: "#FAF9F6", text: "#8A8178", label: mode || "—" };
    return (
        <span className="inline-flex flex-col items-start gap-0.5">
            <span
                className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                style={{ background: m.bg, color: m.text }}
            >
                {m.label}
            </span>
            {status === "pending" && (
                <span className="text-[10px] font-semibold uppercase text-[#C84B31]">Pending</span>
            )}
            {status === "partial" && (
                <span className="text-[10px] font-semibold uppercase text-[#C84B31]">Balance due</span>
            )}
        </span>
    );
}

function membershipTotals(price) {
    const sub = Number(price) || 0;
    const gst = sub * 0.18;
    const cgst = gst / 2;
    const sgst = gst / 2;
    return { sub, cgst, sgst, total: sub + gst };
}

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const primaryBtn =
    "bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]";

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

const deleteBtn =
    "size-8 text-[#B8859C] hover:bg-[#FDE8EC] hover:text-[#4A0E17]";

const iconBtn =
    "size-7 text-[#8A8178] hover:bg-[#FFFEF9] hover:text-[#4A0E17]";

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

export default function Memberships() {
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [members, setMembers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [openPlan, setOpenPlan] = useState(false);
    const [openAssign, setOpenAssign] = useState(false);
    const [collectMember, setCollectMember] = useState(null);
    const [receiptBusy, setReceiptBusy] = useState(null);

    const load = async () => {
        const [p, m, c, o] = await Promise.all([
            api.get("/membership-plans"),
            api.get("/customer-memberships"),
            api.get("/customers"),
            api.get("/outlets").catch(() => ({ data: [] })),
        ]);
        setPlans(p.data);
        setMembers(m.data);
        setCustomers(c.data);
        setOutlets(o.data || []);
    };

    useEffect(() => { load(); }, []);

    const removePlan = async (id) => {
        if (!window.confirm("Delete this plan?")) return;
        await api.delete(`/membership-plans/${id}`);
        load();
    };

    return (
        <div className="space-y-8 p-6 lg:p-8" data-testid="memberships-page">
            <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className={EYEBROW} style={{ color: GOLD }}>
                        Premier
                    </p>
                    <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]" style={{ color: CHOCOLATE }}>
                        Memberships
                    </h1>
                    <GoldDivider className="mt-3 max-w-[200px]" />
                    <p className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-[#8A8178]">
                        <Crown className="size-4 text-[#D4AF37]" />
                        {plans.length} plan{plans.length !== 1 ? "s" : ""}
                        <span className="text-[#E8E4DE]">|</span>
                        <Users className="size-4 text-[#D4AF37]" />
                        {members.length} active member{members.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Dialog open={openAssign} onOpenChange={setOpenAssign}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className={navBtn} data-testid="assign-membership-button">
                                <UserPlus className="mr-1 size-4" /> Sell to customer
                            </Button>
                        </DialogTrigger>
                        <AssignDialog
                            customers={customers}
                            plans={plans}
                            onClose={() => setOpenAssign(false)}
                            onSaved={() => { load(); setOpenAssign(false); }}
                        />
                    </Dialog>
                    <Dialog open={!!collectMember} onOpenChange={(o) => !o && setCollectMember(null)}>
                        {collectMember && (
                            <MembershipCollectDialog
                                member={collectMember}
                                onClose={() => setCollectMember(null)}
                                onSaved={() => { load(); setCollectMember(null); }}
                            />
                        )}
                    </Dialog>
                    <Dialog open={openPlan} onOpenChange={setOpenPlan}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]"
                                data-testid="add-plan-button"
                            >
                                <Plus className="mr-1 size-4" /> New plan
                            </Button>
                        </DialogTrigger>
                        <PlanDialog onClose={() => setOpenPlan(false)} onSaved={() => { load(); setOpenPlan(false); }} />
                    </Dialog>
                </div>
            </header>

            <section>
                <h2 className="mb-4 font-serif text-lg font-bold" style={{ color: CHOCOLATE }}>Plans</h2>
                {plans.length === 0 ? (
                    <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white px-6 py-14 text-center shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
                        <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                        <p className="font-serif text-lg font-bold" style={{ color: CHOCOLATE }}>No plans yet</p>
                        <p className="mt-2 text-sm font-medium text-[#8A8178]">Create a membership plan to start selling.</p>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {plans.map((p, idx) => (
                            <div
                                key={p.id}
                                className={`relative overflow-hidden rounded-xl border bg-white p-6 shadow-[0_4px_22px_rgba(45,41,38,0.04)] transition-colors hover:bg-[#FAF9F6]/40 ${
                                    idx === 1
                                        ? "border-[#D4AF37]/50 bg-gradient-to-br from-[#FFFEF9] to-[#FDF3DC]/35"
                                        : "border-[#E8E4DE]"
                                }`}
                                data-testid={`plan-${p.id}`}
                            >
                                <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                                <div className="flex items-start justify-between">
                                    <Crown className="size-7" style={{ color: idx === 1 ? GOLD : GOLD_SOFT }} />
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className={deleteBtn}
                                        onClick={() => removePlan(p.id)}
                                    >
                                        <Trash2 className="size-4" />
                                    </Button>
                                </div>
                                <h3 className="mt-3 font-serif text-xl font-bold" style={{ color: CHOCOLATE }}>{p.name}</h3>
                                <div className="mt-2 font-serif text-3xl font-bold tabular-nums" style={{ color: CHOCOLATE }}>
                                    {inr(p.price)}
                                </div>
                                <div className="mt-2 text-xs font-semibold text-[#8A8178]">
                                    {p.discount_pct}% off all services
                                </div>
                                <div className="mt-1 text-xs font-medium text-[#9A9088]">
                                    Valid {p.validity_days} days · GST at sale
                                </div>
                                {p.perks && (
                                    <p className="mt-4 rounded-lg border border-[#E8E4DE]/80 bg-[#FFFEF9] px-3 py-2 text-sm font-medium text-[#8A8178]">
                                        {p.perks}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section>
                <h2 className="mb-4 font-serif text-lg font-bold" style={{ color: CHOCOLATE }}>Active members</h2>
                <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
                    <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                    <div className="scrollbar-salon overflow-x-auto pb-1">
                        <table className="w-full min-w-[860px] text-sm">
                            <thead>
                                <tr className="border-b border-[#E8E4DE] bg-gradient-to-r from-[#FFFEF9] to-white text-left text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                                    <th className="px-5 py-3.5">Customer</th>
                                    <th className="px-5 py-3.5">Plan</th>
                                    <th className="px-5 py-3.5">Started</th>
                                    <th className="px-5 py-3.5">Expires</th>
                                    <th className="px-5 py-3.5">Paid</th>
                                    <th className="px-5 py-3.5">Balance</th>
                                    <th className="px-5 py-3.5">Payment</th>
                                    <th className="px-5 py-3.5">Receipt</th>
                                    <th className="px-5 py-3.5" />
                                </tr>
                            </thead>
                            <tbody>
                                {members.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="px-5 py-14 text-center">
                                            <p className="font-serif text-lg font-semibold" style={{ color: MAROON }}>No active memberships</p>
                                            <p className="mt-1 text-sm font-medium text-[#8A8178]">Sell a plan to a customer to get started.</p>
                                        </td>
                                    </tr>
                                )}
                                {members.map((m) => {
                                    const bal = memberBalance(m);
                                    const paid = m.total_paid ?? m.amount_paid;
                                    return (
                                    <tr
                                        key={m.id}
                                        className="border-t border-[#E8E4DE]/80 transition-colors hover:bg-[#FFFEF9]"
                                    >
                                        <td className="px-5 py-3.5 font-bold" style={{ color: CHOCOLATE }}>
                                            {m.customer?.full_name || "—"}
                                        </td>
                                        <td className="px-5 py-3.5 font-semibold" style={{ color: GOLD }}>
                                            {m.plan?.name}
                                        </td>
                                        <td className="px-5 py-3.5 font-medium text-[#8A8178]">
                                            {new Date(m.started_at).toLocaleDateString("en-IN")}
                                        </td>
                                        <td className="px-5 py-3.5 font-medium text-[#8A8178]">
                                            {new Date(m.expires_at).toLocaleDateString("en-IN")}
                                        </td>
                                        <td className="px-5 py-3.5 font-serif font-bold tabular-nums" style={{ color: CHOCOLATE }}>
                                            {paid != null ? inr(paid) : "—"}
                                        </td>
                                        <td className="px-5 py-3.5 font-serif font-bold tabular-nums">
                                            {bal > 0 ? (
                                                <span className="text-[#C84B31]">{inr(bal)}</span>
                                            ) : (
                                                <span className="text-[#8A8178]">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {m.payment_mode ? (
                                                <PaymentBadge mode={m.payment_mode} status={m.payment_status} />
                                            ) : (
                                                <span className="text-xs text-[#9A9088]">Not recorded</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <MembershipReceiptCell
                                                member={m}
                                                outlets={outlets}
                                                user={user}
                                                receiptBusy={receiptBusy}
                                                setReceiptBusy={setReceiptBusy}
                                            />
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {bal > 0 && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className={`h-8 text-xs ${navBtn}`}
                                                    onClick={() => setCollectMember(m)}
                                                    data-testid={`collect-${m.id}`}
                                                >
                                                    <Wallet className="mr-1 size-3.5" />
                                                    Collect
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}

function MembershipReceiptCell({ member, outlets, user, receiptBusy, setReceiptBusy }) {
    const receipts = listMembershipReceipts(member);
    if (!receipts.length) {
        return <span className="text-xs text-[#9A9088]">—</span>;
    }

    const customerFallback = member.customer
        ? { full_name: member.customer.full_name, mobile: member.customer.mobile }
        : undefined;
    const ctx = { outlets, user, customerFallback };

    const run = async (invoiceId, action) => {
        setReceiptBusy(invoiceId);
        try {
            if (action === "print") {
                const ok = await printInvoiceById(invoiceId, ctx);
                if (!ok) toast.error("Allow pop-ups to print the receipt");
            } else {
                await downloadInvoiceById(invoiceId, ctx);
                toast.success("Receipt downloaded");
            }
        } catch {
            toast.error("Could not load receipt");
        } finally {
            setReceiptBusy(null);
        }
    };

    return (
        <div className="space-y-1.5">
            {receipts.map((r) => (
                <div key={r.invoiceId} className="flex flex-wrap items-center gap-1">
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-[#3D2817]">
                        <Receipt className="size-3 text-[#D4AF37]" />
                        {r.invoiceNumber}
                    </span>
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className={iconBtn}
                        title="Print receipt"
                        disabled={receiptBusy === r.invoiceId}
                        onClick={() => run(r.invoiceId, "print")}
                        data-testid={`print-membership-receipt-${r.invoiceId}`}
                    >
                        <Printer className="size-3.5" />
                    </Button>
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className={iconBtn}
                        title="Download receipt"
                        disabled={receiptBusy === r.invoiceId}
                        onClick={() => run(r.invoiceId, "download")}
                        data-testid={`download-membership-receipt-${r.invoiceId}`}
                    >
                        <Download className="size-3.5" />
                    </Button>
                </div>
            ))}
        </div>
    );
}

function PlanDialog({ onClose, onSaved }) {
    const [f, setF] = useState({ name: "", price: 0, discount_pct: 10, validity_days: 365, perks: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/membership-plans", {
                ...f,
                price: +f.price,
                discount_pct: +f.discount_pct,
                validity_days: +f.validity_days,
            });
            toast.success("Saved");
            onSaved();
        } catch {
            toast.error("Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogContent className="rounded-xl border-[#E8E4DE] p-6">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-bold" style={{ color: MAROON }}>New plan</DialogTitle>
                <GoldDivider className="mt-2 max-w-[140px]" />
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Name</Label>
                    <Input className={fieldClass} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="plan-name" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Price (₹)</Label>
                        <Input className={fieldClass} type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="plan-price" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Discount %</Label>
                        <Input className={fieldClass} type="number" value={f.discount_pct} onChange={(e) => setF({ ...f, discount_pct: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Validity (days)</Label>
                        <Input className={fieldClass} type="number" value={f.validity_days} onChange={(e) => setF({ ...f, validity_days: e.target.value })} />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Perks</Label>
                    <Input
                        className={fieldClass}
                        value={f.perks}
                        onChange={(e) => setF({ ...f, perks: e.target.value })}
                        placeholder="e.g. 1 free facial / month"
                    />
                </div>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button type="submit" className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]" disabled={busy} data-testid="save-plan-button">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}

function AssignDialog({ customers, plans, onClose, onSaved }) {
    const [customerId, setCustomerId] = useState("");
    const [planId, setPlanId] = useState("");
    const [payFull, setPayFull] = useState(true);
    const [amountNow, setAmountNow] = useState("");
    const [paymentMode, setPaymentMode] = useState("cash");
    const [paymentRef, setPaymentRef] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const selectedPlan = plans.find((p) => p.id === planId);
    const totals = selectedPlan ? membershipTotals(selectedPlan.price) : null;
    const collecting = payFull ? totals?.total : Number(amountNow) || 0;
    const balancePreview = totals ? Math.max(0, totals.total - collecting) : 0;

    const submit = async (e) => {
        e.preventDefault();
        if (!customerId || !planId || !totals) return;
        if (!payFull && (collecting <= 0 || collecting >= totals.total)) {
            toast.error("Enter an amount less than the full total for partial payment");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                customer_id: customerId,
                plan_id: planId,
                payment_mode: paymentMode,
                payment_ref: paymentRef.trim() || undefined,
                notes: notes.trim() || undefined,
            };
            if (!payFull) payload.amount_paid_now = collecting;

            const r = await api.post("/customer-memberships", payload);
            const inv = r.data?.invoice_number;
            const bal = r.data?.balance_due;
            if (bal > 0) {
                toast.success(`Part payment recorded · ${inv} · Balance ${inr(bal)}`);
            } else {
                toast.success(inv ? `Membership sold · ${inv}` : "Membership activated");
            }
            onSaved();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Failed to sell membership");
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogContent className="scrollbar-dialog max-h-[min(90vh,640px)] overflow-y-auto overscroll-contain rounded-xl border-[#E8E4DE] p-6 pr-5 sm:max-w-md">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-bold" style={{ color: MAROON }}>Sell membership</DialogTitle>
                <GoldDivider className="mt-2 max-w-[140px]" />
                <p className="pt-1 text-xs font-medium text-[#8A8178]">
                    Pay in full today, or collect a part now and installments later from Active members.
                </p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Customer</Label>
                    <Select value={customerId} onValueChange={setCustomerId}>
                        <SelectTrigger className={fieldClass} data-testid="assign-customer">
                            <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                            {customers.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.full_name} · {c.mobile}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Plan</Label>
                    <Select value={planId} onValueChange={setPlanId}>
                        <SelectTrigger className={fieldClass} data-testid="assign-plan">
                            <SelectValue placeholder="Select plan" />
                        </SelectTrigger>
                        <SelectContent>
                            {plans.map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.name} · {inr(p.price)} + GST</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {totals && (
                    <div className="rounded-xl border border-[#E8E4DE] bg-[#FFFEF9] p-4 text-sm">
                        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#9A7B10]">
                            <IndianRupee className="size-3.5" />
                            Payment summary
                        </p>
                        <ul className="mt-3 space-y-1.5 font-medium text-[#5C534C]">
                            <li className="flex justify-between">
                                <span className="text-[#8A8178]">Plan price</span>
                                <span>{inr(totals.sub)}</span>
                            </li>
                            <li className="flex justify-between">
                                <span className="text-[#8A8178]">CGST (9%)</span>
                                <span>{inr(totals.cgst)}</span>
                            </li>
                            <li className="flex justify-between">
                                <span className="text-[#8A8178]">SGST (9%)</span>
                                <span>{inr(totals.sgst)}</span>
                            </li>
                            <li className="flex justify-between border-t border-[#E8E4DE] pt-2 font-serif text-base font-bold" style={{ color: CHOCOLATE }}>
                                <span>Total due</span>
                                <span style={{ color: MAROON }}>{inr(totals.total)}</span>
                            </li>
                            {!payFull && collecting > 0 && (
                                <>
                                    <li className="flex justify-between text-[#C84B31]">
                                        <span>Collecting now</span>
                                        <span>{inr(collecting)}</span>
                                    </li>
                                    <li className="flex justify-between font-semibold">
                                        <span className="text-[#8A8178]">Balance later</span>
                                        <span>{inr(balancePreview)}</span>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                )}

                <div className="space-y-3 rounded-xl border border-[#E8E4DE] bg-[#FAF9F6]/60 p-4">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>
                        Payment type
                    </Label>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant={payFull ? "default" : "outline"}
                            className={payFull ? primaryBtn.replace("w-full ", "") : navBtn}
                            onClick={() => setPayFull(true)}
                        >
                            Pay in full
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={!payFull ? "default" : "outline"}
                            className={!payFull ? primaryBtn.replace("w-full ", "") : navBtn}
                            onClick={() => setPayFull(false)}
                        >
                            Partial / installment
                        </Button>
                    </div>
                    {!payFull && totals && (
                        <div className="space-y-2 pt-1">
                            <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>
                                Amount collecting now (₹)
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                max={totals.total - 0.01}
                                step="0.01"
                                className={fieldClass}
                                value={amountNow}
                                onChange={(e) => setAmountNow(e.target.value)}
                                placeholder={`e.g. ${Math.round(totals.total / 2)}`}
                                data-testid="assign-amount-now"
                            />
                            <p className="text-[11px] font-medium text-[#8A8178]">
                                Tip: 50% now — collect the rest anytime from the members table.
                            </p>
                        </div>
                    )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Payment method</Label>
                        <Select value={paymentMode} onValueChange={setPaymentMode}>
                            <SelectTrigger className={fieldClass} data-testid="assign-payment-mode">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="upi">UPI</SelectItem>
                                <SelectItem value="card">Card</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {(paymentMode === "upi" || paymentMode === "card") && (
                        <div className="space-y-2 sm:col-span-2">
                            <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>
                                {paymentMode === "upi" ? "UPI reference" : "Card / txn reference"}
                            </Label>
                            <Input
                                className={fieldClass}
                                value={paymentRef}
                                onChange={(e) => setPaymentRef(e.target.value)}
                                placeholder={paymentMode === "upi" ? "e.g. UTR / txn ID" : "Last 4 digits or approval code"}
                                data-testid="assign-payment-ref"
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Notes (optional)</Label>
                    <Input
                        className={fieldClass}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Internal note for receipt"
                    />
                </div>

                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button
                        type="submit"
                        className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]"
                        disabled={busy || !customerId || !planId}
                        data-testid="save-assign-button"
                    >
                        {busy ? "Processing…" : payFull ? "Collect payment & activate" : "Collect part payment & activate"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
